import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { ApplicationConfigService } from 'src/app/core/config/application-config.service';
import { AuditLogService } from 'src/app/admin/audit-log/audit-log.service';
import { AuditLogFilter } from 'src/app/admin/audit-log/audit-log.model';
import { CommonService } from 'src/app/shared/services/common.service';

import { AUDIT_BRANCHES, AUDIT_FEATURES, AUDIT_USERS, buildAuditLog } from '../fixtures/audit-log.fixture';
import { MockDb } from '../mock-db';

const MOCK_LATENCY_MS = 250;

function envelope<T>(data: T) {
  return { responseCode: 'S100000', responseMessage: 'Operation successful', data };
}

function respond<T>(data: T): Observable<any> {
  return of(envelope(data)).pipe(delay(MOCK_LATENCY_MS));
}

/** Audit Log → All Admin Log. Stands in for AuditLogService when `environment.useMock` is true. */
@Injectable()
export class MockAuditLogService extends AuditLogService {
  constructor(http: HttpClient, commonService: CommonService, applicationConfigService: ApplicationConfigService) {
    super(http, commonService, applicationConfigService);
  }

  override getAuditLogList(query: AuditLogFilter): Observable<any> {
    const filtered = this.rows().filter((row) => this.matches(row, query));

    const pageSize = Number(query?.pageSize) > 0 ? Number(query.pageSize) : 10;
    const pageNumber = Number(query?.pageNumber) >= 0 ? Number(query.pageNumber) : 0;
    const start = pageNumber * pageSize;

    return respond({
      data: filtered.slice(start, start + pageSize),
      currentPage: pageNumber,
      pageSize,
      totalItems: filtered.length,
    });
  }

  private matches(row: Record<string, any>, query: AuditLogFilter): boolean {
    if (query?.createdBy && query.createdBy !== 'All' && row['createdBy'] !== query.createdBy) {
      return false;
    }
    if (query?.feature && row['feature'] !== query.feature) {
      return false;
    }
    if (query?.operation && row['operation'] !== query.operation) {
      return false;
    }
    if (query?.status && row['status'] !== query.status) {
      return false;
    }
    if (query?.branch && query.branch !== 'All' && row['branch'] !== query.branch) {
      return false;
    }

    // operationDate is 'YYYY-MM-DD HH:mm:ss'; the filter sends plain 'YYYY-MM-DD'.
    const day = String(row['operationDate']).slice(0, 10);
    if (query?.fromDate && day < String(query.fromDate).slice(0, 10)) {
      return false;
    }
    if (query?.toDate && day > String(query.toDate).slice(0, 10)) {
      return false;
    }
    return true;
  }

  override getFeatureDropDown(): Observable<any> {
    return respond(AUDIT_FEATURES);
  }

  override getAllBranch(): Observable<any> {
    return respond(AUDIT_BRANCHES);
  }

  override getAllUsers(): Observable<any> {
    return respond(AUDIT_USERS);
  }

  override download(_query: any): Observable<any> {
    return of(new Blob([], { type: 'application/pdf' })).pipe(delay(MOCK_LATENCY_MS));
  }

  private rows(): Record<string, any>[] {
    return [...buildAuditLog(), ...(MockDb.get<Record<string, any>[]>('fdrAuditLogs') ?? [])].sort((a, b) =>
      String(b['operationDate']).localeCompare(String(a['operationDate'])),
    );
  }
}
